import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';


const Scrim = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const Sheet = styled(motion.div)`
  width: 100%;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  padding: 12px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  max-height: 85vh;
  overflow-y: auto;

  @media (min-width: 769px) {
    max-width: 520px;
    border-radius: 20px;
    padding: 28px 32px;
  }
`;

const DragHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #d1d5db;
  margin: 0 auto 16px;

  @media (min-width: 769px) {
    display: none;
  }
`;

const SheetTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 16px;
  text-align: center;

  @media (min-width: 769px) {
    font-size: 1.35rem;
    margin-bottom: 20px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 769px) {
    flex-direction: row;
    gap: 16px;
  }
`;

const OptionCard = styled(motion.button)`
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 16px;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${props => props.$accent};
  border-radius: 16px;
  background: #f8fafc;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    background: #f0f5ff;
    border-color: rgba(66, 133, 244, 0.15);
  }

  @media (min-width: 769px) {
    flex-direction: column;
    align-items: stretch;
    padding: 22px 20px;
  }
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  background: ${props => props.$bg};
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.span`
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
`;

const CardSubtitle = styled.span`
  display: block;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.4;
`;

const Chevron = styled.span`
  color: #94a3b8;
  font-size: 18px;
  align-self: center;
  flex-shrink: 0;

  @media (min-width: 769px) {
    align-self: flex-end;
    margin-top: 8px;
  }
`;

const mobileSheetVariants = {
  hidden: { y: '100%' },
  visible: { y: 0, transition: { type: 'spring', damping: 28, stiffness: 300 } },
  exit: { y: '100%', transition: { duration: 0.2 } },
};

const desktopSheetVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

const scrimVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

function CreateTripSheet({ isOpen, onClose }) {
  const navigate = useNavigate();

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  const handleOption = (path) => {
    onClose();
    navigate(path);
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <AnimatePresence>
      {isOpen && (
        <Scrim
          variants={scrimVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <Sheet
            variants={isMobile ? mobileSheetVariants : desktopSheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <DragHandle />
            <SheetTitle>How would you like to start?</SheetTitle>
            <CardsContainer>
              <OptionCard
                $accent="#1A73E8"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOption('/itineraries?create=true')}
              >
                <IconCircle $bg="rgba(66, 133, 244, 0.1)">✈️</IconCircle>
                <CardContent>
                  <CardTitle>Create My Trip</CardTitle>
                  <CardSubtitle>I know where I want to go — pick destination, duration &amp; budget</CardSubtitle>
                </CardContent>
                <Chevron>›</Chevron>
              </OptionCard>

              <OptionCard
                $accent="#8B5CF6"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleOption('/discover')}
              >
                <IconCircle $bg="rgba(139, 92, 246, 0.1)">✨</IconCircle>
                <CardContent>
                  <CardTitle>Surprise Me</CardTitle>
                  <CardSubtitle>Help me discover the perfect place — tell us your vibe</CardSubtitle>
                </CardContent>
                <Chevron>›</Chevron>
              </OptionCard>
            </CardsContainer>
          </Sheet>
        </Scrim>
      )}
    </AnimatePresence>
  );
}

export default CreateTripSheet;
